/**
 * El talón de recepción, para imprimir y darle al cliente.
 *
 * POR QUÉ ESTO ES UN PAPEL Y NO UNA PANTALLA MÁS
 * ----------------------------------------------
 * Es el único momento en que el local y el cliente firman lo mismo. El equipo
 * se queda acá y la persona se va con una constancia de qué dejó, en qué
 * estado, cuánto pagó y bajo qué condiciones. Sin eso, todo lo que pase después
 * —el plazo, la garantía, el equipo que nadie retira— es la palabra de uno
 * contra la del otro.
 *
 * LA LETRA CHICA VA EN EL PAPEL, NO EN UNA PANTALLA
 * -------------------------------------------------
 * Las condiciones se imprimen al pie, completas. Aceptarlas es recibir el
 * talón: por eso la última línea lo dice explícitamente. Un checkbox en el
 * sistema no prueba nada, porque lo aprieta el que atiende.
 *
 * CÓMO SE IMPRIME
 * ---------------
 * `window.print()` y una hoja de estilos de impresión, sin librerías. En el
 * celular el diálogo del sistema ofrece "Guardar en Archivos", así que el PDF
 * sale gratis. Es el mismo enfoque que ya había decidido la spec del remito.
 *
 * Lo que se imprime NO es la vista previa del diálogo sino una copia colgada
 * directo del `body` (`paraImprimir`). La del diálogo vive adentro de una caja
 * fija, corrida con `translate` y con scroll propio: al imprimir quedaba
 * recortada por esa caja y la hoja salía en blanco.
 */
// Las condiciones viven en `@tora/shared`: las imprime este papel y las muestra
// la página del talón que se manda por WhatsApp (`/t/:token`, en el backend).
import { ACEPTACION_TALON, CONDICIONES_TALON, formatear } from "@tora/shared";

import { aNumero, type EmpresaConfig } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

import { leerPlata, nombreCliente, nombreOrden, type OrdenTaller } from "./logica";

export interface TalonReparacionProps {
  orden: OrdenTaller;
  empresa?: EmpresaConfig | null;
  /** Para escribirle si hace falta. No sale impreso si no está. */
  telefono?: string | null;
  /** La copia que va a la impresora. Hay una sola por página: la busca la hoja de impresión. */
  paraImprimir?: boolean;
}

export function TalonReparacion({
  orden,
  empresa,
  telefono,
  paraImprimir = false,
}: TalonReparacionProps): JSX.Element {
  const plata = leerPlata(orden);
  const diagnostico = aNumero(orden.diagnostico_importe);
  const monedaDiag = orden.diagnostico_moneda ?? "USD";

  return (
    <article
      id={paraImprimir ? "talon-impresion" : undefined}
      className="talon mx-auto max-w-[680px] bg-white p-6 text-black"
    >
      <header className="flex items-start justify-between gap-4 border-b border-black/20 pb-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">
            {empresa?.nombre ?? "MANZANA MORDIDA"}
          </h1>
          <p className="text-[11px] leading-snug text-black/70">
            {[empresa?.direccion, empresa?.telefono, empresa?.instagram]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-base font-semibold">{nombreOrden(orden)}</p>
          <p className="text-[11px] text-black/70">{formatearFecha(orden.fecha_ingreso)}</p>
        </div>
      </header>

      <h2 className="mt-3 text-sm font-semibold uppercase tracking-wide">
        Constancia de recepción
      </h2>

      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-[12px] sm:grid-cols-2">
        <Dato termino="Cliente" valor={nombreCliente(orden)} />
        {telefono ? <Dato termino="Contacto" valor={telefono} /> : null}
        <Dato termino="Equipo" valor={orden.equipo} />
        {orden.imei ? <Dato termino="IMEI o serie" valor={orden.imei} /> : null}
        {orden.color ? <Dato termino="Color" valor={orden.color} /> : null}
        {orden.accesorios ? <Dato termino="Accesorios" valor={orden.accesorios} /> : null}
      </dl>

      <div className="mt-3 border-t border-black/10 pt-2 text-[12px]">
        <p className="font-medium">Lo que dice el cliente</p>
        <p className="text-black/80">{orden.falla_declarada}</p>
      </div>

      <div className="mt-3 border-t border-black/10 pt-2 text-[12px]">
        {diagnostico > 0 ? (
          <p>
            <span className="font-medium">Diagnóstico y presupuesto: </span>
            {formatear(diagnostico, monedaDiag)}
            {orden.diagnostico_motivo ? (
              <span className="text-black/70">
                {" "}
                · pendiente de pago ({orden.diagnostico_motivo})
              </span>
            ) : (
              <span className="text-black/70"> · abonado. Se toma a cuenta del total.</span>
            )}
          </p>
        ) : null}
        {plata.precio ? (
          <p>
            <span className="font-medium">Presupuesto: </span>
            {formatear(plata.precio, plata.moneda)}
          </p>
        ) : (
          <p className="text-black/70">
            El presupuesto se informa una vez revisado el equipo, dentro del plazo estimado.
          </p>
        )}
      </div>

      {/* ── La letra chica ── */}
      <section className="mt-4 border-t border-black/20 pt-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide">
          Condiciones del servicio
        </h3>
        <ul className="mt-1 space-y-0.5 text-[9px] leading-tight text-black/80">
          {CONDICIONES_TALON.map((c) => (
            <li key={c.titulo}>
              <span className="font-semibold">{c.titulo}.</span> {c.texto}
            </li>
          ))}
        </ul>
        <p className="mt-1.5 text-[9px] font-medium leading-tight">{ACEPTACION_TALON}</p>
      </section>

      <div className="mt-8 grid grid-cols-2 gap-8 text-[10px]">
        <Firma rotulo="Firma del cliente" />
        <Firma rotulo="Recibió" />
      </div>
    </article>
  );
}

function Dato({ termino, valor }: { termino: string; valor: string }): JSX.Element {
  return (
    <div className="flex gap-1.5">
      <dt className="shrink-0 text-black/60">{termino}:</dt>
      <dd className="min-w-0 font-medium">{valor}</dd>
    </div>
  );
}

function Firma({ rotulo }: { rotulo: string }): JSX.Element {
  return (
    <div>
      <div className="h-8 border-b border-black/40" />
      <p className="mt-1 text-black/60">{rotulo}</p>
    </div>
  );
}
